import { z } from 'zod';

import { PersonNameSchema, ProfilePreferencesSchema, TaggedPreferencesSchema } from './shared-schemas.js';
import { ZodGoogleCabinClass } from './zod-types.js';

/** A Zod schema for passport information. */
export const PassportInfoSchema = z.object({
  number: z.string().optional(),
  expiryDate: z.string().optional(),
  nationality: z.string().optional(),
});
export type PassportInfo = z.infer<typeof PassportInfoSchema>;

/**
 * A Zod schema for a traveler's profile, including personal details and travel preferences.
 *
 * This is the single schema for all traveler-related data on the user profile.
 * Travel preferences (flights, hotels, destinations, etc.) were previously in a
 * separate TravelPreferencesSchema but are now unified here.
 */
export const TravelerProfileSchema = PersonNameSchema.extend({
  // Personal info
  dateOfBirth: z.string().optional(),
  passportInfo: PassportInfoSchema.optional(),
  knownTravelerNumber: z.string().optional(),

  // Travel preferences
  hotelPreferences: TaggedPreferencesSchema,
  preferredAirports: TaggedPreferencesSchema,
  domesticFlightClass: z.array(ZodGoogleCabinClass).optional(),
  internationalFlightClass: z.array(ZodGoogleCabinClass).optional(),
  wishlistDestinations: TaggedPreferencesSchema,
  keyDates: TaggedPreferencesSchema,
  travelCreditCards: TaggedPreferencesSchema,

  // General preferences (dietary, accessibility, interests, etc.)
  preferences: ProfilePreferencesSchema,
});

/** The TypeScript type for a traveler's profile. */
export type TravelerProfile = z.infer<typeof TravelerProfileSchema>;

/** Relationship types for additional travellers. */
export const RelationshipSchema = z.enum([
  'spouse',
  'partner',
  'son',
  'daughter',
  'child',
  'parent',
  'sibling',
  'friend',
  'other',
]);
export type Relationship = z.infer<typeof RelationshipSchema>;

/** A Zod schema for an additional traveller (family member, companion, etc.). */
export const AdditionalTravellerSchema = TravelerProfileSchema.extend({
  /**
   * Stable identity for a companion, so edits and preferences target the right person even after the
   * list is reordered or someone is renamed. Assigned by the server on save; optional only because
   * companions saved before ids existed are given one by the next save or the preferences migration.
   */
  id: z.string().min(1).optional(),
  relationship: RelationshipSchema,
  age: z.number().int().min(0).max(120).optional(),
});
export type AdditionalTraveller = z.infer<typeof AdditionalTravellerSchema>;
